// Function index:
//   VIEWPORT_PAD_PX                  — extra world-pixels around the viewport
//                                      a tile is kept mounted for.
//   POOL_MAX                         — cap on free Graphics retained for reuse.
//   createCullingState()             — fresh per-scene culling state.
//   relayout(ctx)                    — mount tiles entering the viewport,
//                                      recycle tiles leaving it. O(loaded).
//   visibleWorldRect(app, camera)    — world-space rect the camera currently
//                                      shows, padded by VIEWPORT_PAD_PX.
//                                      Shared with IsoRenderer (hex-scene/iso).
//
// Phase B3 of the massive-world plan. At 1M-hex scale the scene can hold
// at most a few thousand mounted Graphics; we lazy-mount only tiles the
// camera actually shows, and recycle their Graphics back to a pool when
// they slide off-screen. The set of *known* hexes (hexData) is bounded
// separately by the lazy bootstrap + streaming endpoint.

import { Application, Container, Graphics, Sprite, type FederatedPointerEvent, type Texture } from 'pixi.js';
import { HEX_SIZE, HexTile, FOGGY_DIM, UNDISCOVERED_DIM, baseColor, dim, drawTile, type Visibility } from './palette.js';
import { cancelTween } from './anim.js';
import type { HexInput } from './types.js';

export const VIEWPORT_PAD_PX = 200;
export const POOL_MAX = 512;

// Pointy-top hex bounding box — the size a terrain sprite is scaled to cover.
const HEX_PX_W = Math.sqrt(3) * HEX_SIZE;
const HEX_PX_H = 2 * HEX_SIZE;

export interface MountedTile {
  g: Graphics;
  // Phase 5: optional terrain sprite drawn over the polygon. Present only when
  // the sprite layer is active AND a texture exists for the terrain.
  sprite?: Sprite;
  terrain: string;
  discovered: boolean;
}

export interface CullingState {
  mountedTiles: Map<string, MountedTile>;
  graphicsPool: Graphics[];
  spritePool: Sprite[];
}

export function createCullingState(): CullingState {
  return { mountedTiles: new Map(), graphicsPool: [], spritePool: [] };
}

export interface CullingContext {
  state: CullingState;
  app: Application;
  camera: Container;
  tileLayer: Container;
  hexData: Map<string, HexInput>;
  visibilityFor: (key: string, discovered: boolean) => Visibility;
  discoveredKeys: Set<string>;
  onTileHover: (q: number, r: number, key: string) => void;
  onTileHoverOut: (key: string) => void;
  onTileClick: (q: number, r: number) => void;
  onTileRightClick: (q: number, r: number) => void;
  // Phase 5 (optional): terrain → Texture. When omitted (the default / flag
  // off / no atlas) tiles render as vector polygons exactly as before.
  terrainTexture?: (terrain: string) => Texture | null;
}

// World-space rectangle visible through the camera, padded by VIEWPORT_PAD_PX
// so tiles just outside the edge stay alive while the user pans.
export function visibleWorldRect(app: Application, camera: Container): {
  minX: number; maxX: number; minY: number; maxY: number;
} {
  const w = app.renderer.width;
  const h = app.renderer.height;
  const s = camera.scale.x || 1;
  const pad = VIEWPORT_PAD_PX / s;
  return {
    minX: (-camera.x) / s - pad,
    maxX: (w - camera.x) / s + pad,
    minY: (-camera.y) / s - pad,
    maxY: (h - camera.y) / s + pad,
  };
}

// Screen-space slop separating a right-CLICK from a right-DRAG pan. The
// camera pans WITH the pointer, so the same tile stays under the cursor
// for the whole drag and Pixi still fires rightclick on release —
// press+release-on-same-object alone can't tell the two apart.
const RIGHT_CLICK_SLOP_PX = 6;

// One pointer, one press: shared across tiles is safe.
let rightDownAt: { x: number; y: number } | null = null;

function attachHandlers(
  g: Graphics,
  q: number,
  r: number,
  key: string,
  ctx: CullingContext,
): void {
  g.eventMode = 'static';
  g.on('pointerover', () => ctx.onTileHover(q, r, key));
  g.on('pointerout', () => ctx.onTileHoverOut(key));
  g.on('pointerdown', (e: FederatedPointerEvent) => {
    if (e.button !== 0) return;
    e.stopPropagation();
    ctx.onTileClick(q, r);
  });
  g.on('rightdown', (e: FederatedPointerEvent) => {
    rightDownAt = { x: e.clientX, y: e.clientY };
  });
  g.on('rightclick', (e: FederatedPointerEvent) => {
    if (rightDownAt && Math.hypot(e.clientX - rightDownAt.x, e.clientY - rightDownAt.y) > RIGHT_CLICK_SLOP_PX) return;
    e.stopPropagation();
    ctx.onTileRightClick(q, r);
  });
}

function acquireGraphics(state: CullingState): Graphics {
  return state.graphicsPool.pop() ?? new Graphics();
}

function releaseGraphics(state: CullingState, g: Graphics, layer: Container): void {
  // Stop any in-flight fog-fade so the tween callback doesn't keep ticking
  // on a recycled tile, and reset alpha so the pooled Graphics is reusable.
  cancelTween(g);
  g.alpha = 1;
  layer.removeChild(g);
  g.clear();
  g.removeAllListeners();
  if (state.graphicsPool.length < POOL_MAX) state.graphicsPool.push(g);
  else g.destroy();
}

function acquireSprite(state: CullingState): Sprite {
  return state.spritePool.pop() ?? new Sprite();
}

function releaseSprite(state: CullingState, sp: Sprite, layer: Container): void {
  layer.removeChild(sp);
  sp.tint = 0xffffff;
  if (state.spritePool.length < POOL_MAX) state.spritePool.push(sp);
  else sp.destroy();
}

// Create / update / drop the terrain sprite for a mounted tile. No-op (and
// releases any existing sprite) when the sprite layer is off or the terrain
// has no texture, so the vector polygon is the sole render in that case.
function syncTileSprite(ctx: CullingContext, mounted: MountedTile, hx: number, hy: number, vis: Visibility): void {
  const tex = ctx.terrainTexture ? ctx.terrainTexture(mounted.terrain) : null;
  if (!tex) {
    if (mounted.sprite) {
      releaseSprite(ctx.state, mounted.sprite, ctx.tileLayer);
      mounted.sprite = undefined;
    }
    return;
  }
  let sp = mounted.sprite;
  if (!sp) {
    sp = acquireSprite(ctx.state);
    sp.eventMode = 'none'; // clicks pass through to the polygon underneath
    sp.anchor.set(0.5);
    ctx.tileLayer.addChild(sp);
    mounted.sprite = sp;
  }
  sp.texture = tex;
  sp.x = hx;
  sp.y = hy;
  sp.width = HEX_PX_W;
  sp.height = HEX_PX_H;
  // Reuse the polygon's fog dimming as a sprite tint so fog reads consistently.
  sp.tint = vis === 'visible' ? 0xffffff : dim(0xffffff, vis === 'foggy' ? FOGGY_DIM : UNDISCOVERED_DIM);
}

export function relayout(ctx: CullingContext): void {
  const rect = visibleWorldRect(ctx.app, ctx.camera);
  const needed = new Set<string>();

  for (const [key, data] of ctx.hexData) {
    const hex = new HexTile({ q: data.q, r: data.r });
    if (hex.x < rect.minX || hex.x > rect.maxX) continue;
    if (hex.y < rect.minY || hex.y > rect.maxY) continue;
    needed.add(key);

    const existing = ctx.state.mountedTiles.get(key);
    const discovered = ctx.discoveredKeys.has(key);
    const vis = ctx.visibilityFor(key, discovered);
    if (existing) {
      if (existing.terrain === data.terrain && existing.discovered === discovered) continue;
      existing.g.clear();
      drawTile(existing.g, hex.corners, baseColor(data.terrain, vis));
      existing.terrain = data.terrain;
      existing.discovered = discovered;
      syncTileSprite(ctx, existing, hex.x, hex.y, vis);
    } else {
      const g = acquireGraphics(ctx.state);
      drawTile(g, hex.corners, baseColor(data.terrain, vis));
      attachHandlers(g, data.q, data.r, key, ctx);
      ctx.tileLayer.addChild(g);
      const mounted: MountedTile = { g, terrain: data.terrain, discovered };
      ctx.state.mountedTiles.set(key, mounted);
      syncTileSprite(ctx, mounted, hex.x, hex.y, vis);
    }
  }

  for (const [key, tile] of ctx.state.mountedTiles) {
    if (needed.has(key)) continue;
    releaseGraphics(ctx.state, tile.g, ctx.tileLayer);
    if (tile.sprite) releaseSprite(ctx.state, tile.sprite, ctx.tileLayer);
    ctx.state.mountedTiles.delete(key);
  }
}

// Redraw a single tile by key if it's currently mounted. Used by piecewise
// state updates (terrain change, fog flip) that don't need a full relayout.
export function redrawTile(ctx: CullingContext, key: string): void {
  const data = ctx.hexData.get(key);
  if (!data) return;
  const mounted = ctx.state.mountedTiles.get(key);
  if (!mounted) return;
  const hex = new HexTile({ q: data.q, r: data.r });
  const discovered = ctx.discoveredKeys.has(key);
  const vis = ctx.visibilityFor(key, discovered);
  mounted.g.clear();
  drawTile(mounted.g, hex.corners, baseColor(data.terrain, vis));
  mounted.terrain = data.terrain;
  mounted.discovered = discovered;
  syncTileSprite(ctx, mounted, hex.x, hex.y, vis);
}
